import React, { useState } from 'react';
import AdminLayout from '../AdminLayout';
import './AdminBlogs.css';

const AdminBlogs = () => {
  const getInitialBlogs = () => {
    const storedBlogs = localStorage.getItem('adminBlogs');
    if (storedBlogs) {
      return JSON.parse(storedBlogs);
    }
    return [
      {
        id: 1,
        title: 'Cleaning Tips for a Healthy Home',
        content: 'Learn the best practices for maintaining a clean and healthy living environment...',
        author: 'Admin',
        date: '2024-01-15',
        status: 'published',
        image: null
      },
      {
        id: 2,
        title: 'Commercial Cleaning Services Guide',
        content: 'Comprehensive guide to commercial cleaning services for businesses...',
        author: 'Admin',
        date: '2024-01-10',
        status: 'draft',
        image: null
      }
    ];
  };

  const [blogs, setBlogs] = useState(getInitialBlogs);

  const saveBlogsToStorage = (blogsArray) => {
    localStorage.setItem('adminBlogs', JSON.stringify(blogsArray));
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('blogsUpdated'));
  };

  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [newBlog, setNewBlog] = useState({
    title: '',
    content: '',
    author: 'Admin',
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (editingBlog) {
      setEditingBlog({ ...editingBlog, [name]: value });
    } else {
      setNewBlog({ ...newBlog, [name]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let updatedBlogs;
    if (editingBlog) {
      updatedBlogs = blogs.map(blog =>
        blog.id === editingBlog.id ? { ...editingBlog, date: new Date().toISOString().split('T')[0] } : blog
      );
      setBlogs(updatedBlogs);
      setEditingBlog(null);
    } else {
      const blog = {
        id: blogs.length + 1,
        ...newBlog,
        date: new Date().toISOString().split('T')[0],
        status: 'draft'
      };
      updatedBlogs = [...blogs, blog];
      setBlogs(updatedBlogs);
      setNewBlog({ title: '', content: '', author: 'Admin', image: null });
    }
    saveBlogsToStorage(updatedBlogs);
    setShowForm(false);
    setImagePreview(null);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setImagePreview(blog.image);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      const updatedBlogs = blogs.filter(blog => blog.id !== id);
      setBlogs(updatedBlogs);
      saveBlogsToStorage(updatedBlogs);
    }
  };

  const toggleStatus = (id) => {
    const updatedBlogs = blogs.map(blog =>
      blog.id === id ? { ...blog, status: blog.status === 'published' ? 'draft' : 'published' } : blog
    );
    setBlogs(updatedBlogs);
    saveBlogsToStorage(updatedBlogs);
  };

  const cancelEdit = () => {
    setEditingBlog(null);
    setShowForm(false);
    setNewBlog({ title: '', content: '', author: 'Admin', image: null });
    setImagePreview(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        if (editingBlog) {
          setEditingBlog({ ...editingBlog, image: reader.result });
        } else {
          setNewBlog({ ...newBlog, image: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-blogs">
        <div className="blogs-header">
          <h1>Blog Management</h1>
          <button
            className="add-blog-btn"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Add New Blog'}
          </button>
        </div>

        {showForm && (
          <form className="blog-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={editingBlog ? editingBlog.title : newBlog.title}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="content">Content</label>
              <textarea
                id="content"
                name="content"
                value={editingBlog ? editingBlog.content : newBlog.content}
                onChange={handleInputChange}
                rows="10"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="author">Author</label>
              <input
                type="text"
                id="author"
                name="author"
                value={editingBlog ? editingBlog.author : newBlog.author}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="image">Blog Image</label>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
              />
              {(imagePreview || (editingBlog && editingBlog.image)) && (
                <div className="image-preview">
                  <img
                    src={imagePreview || (editingBlog && editingBlog.image)}
                    alt="Blog preview"
                    className="preview-image"
                  />
                </div>
              )}
            </div>
            <div className="form-actions">
              <button type="submit" className="save-btn">
                {editingBlog ? 'Update Blog' : 'Save Blog'}
              </button>
              <button type="button" className="cancel-btn" onClick={cancelEdit}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="blogs-table-container">
          <table className="blogs-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Author</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map(blog => (
                <tr key={blog.id}>
                  <td>
                    {blog.image ? (
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="table-image"
                      />
                    ) : (
                      <span className="no-image">No Image</span>
                    )}
                  </td>
                  <td>{blog.title}</td>
                  <td>{blog.author}</td>
                  <td>{blog.date}</td>
                  <td>
                    <span className={`status ${blog.status}`}>
                      {blog.status}
                    </span>
                  </td>
                  <td>
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(blog)}
                    >
                      Edit
                    </button>
                    <button
                      className="action-btn toggle"
                      onClick={() => toggleStatus(blog.id)}
                    >
                      {blog.status === 'published' ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(blog.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogs;
